/**
 * The web surface ramp, derived from the mobile palette.
 *
 * Mobile needs eleven colours because it is one screen. The web has cards,
 * docks, input wells, hover states and hairlines, so it needs a ladder — but
 * inventing one by hand is how a codebase ends up with three competing ramps and
 * five hundred hex literals, which is exactly the state this replaces.
 *
 * So every value here is COMPUTED from `palette`, in units of OKLCH lightness,
 * and the derivation is the code. Two consequences worth knowing:
 *
 *   - Editing a value means editing its derivation, which is visible in review.
 *   - `surfaces.test.ts` asserts the ladder is monotonic and evenly stepped, so
 *     "each surface is a clear, equal perceptual step above the last" is a
 *     property the suite checks rather than a claim in a comment.
 *
 * The ladder runs the opposite way to the old dark editor: the page sits near
 * the top of the range and surfaces climb toward white, rather than a near-black
 * stage with everything lifting out of it.
 */
import { adjustLightness, mixOver, oklchToRgb, rgbToHex, rgbToOklch } from './color';
import { palette } from './palette';

/** One perceptual step. Small, because on a light ground the eye resolves far
 *  finer lightness differences than it does near black. */
const STEP = 0.022;

/**
 * One cool family, four even steps.
 *
 * `paper` was the app's warm bone ground (#F1EEE7, hue 87) sitting inside a cool
 * glacier page (hue 262). On a phone that contrast is a single deliberate note —
 * paper under line art, once. Spread across a whole website it stops reading as
 * a choice and starts reading as two palettes that do not match, most obviously
 * where the generated field's ground meets the page around it.
 *
 * So the web's paper is a cool white derived from the page, and the temperature
 * split is gone. Mobile keeps `palette.ground` untouched; this is a divergence
 * in surface, not in palette.
 *
 * There used to be a fifth surface (`field`, for inputs) which collided with
 * `raised` once everything came from one hue — three tiers above the page is one
 * more than a light system can show. Inputs and popovers share the brightest.
 */
/**
 * A step up the ladder, desaturating as it approaches white.
 *
 * Holding chroma flat while raising lightness pushes the colour out of the sRGB
 * gamut near white; the channels clamp, and the clamp moves the hue — the two
 * brightest surfaces came out 28° away from the page and the "one cool family"
 * check caught it. Tapering chroma is also just what paper does: the closer a
 * surface gets to white, the less tint it can hold.
 */
function lift(steps: number): string {
  const { l, c, h } = rgbToOklch(palette.page);
  const taper = Math.max(0, 1 - steps * 0.35);
  return rgbToHex(oklchToRgb({ l: l + STEP * steps, c: c * taper, h }));
}

export const surfaces = {
  /** Pressed and hover states. Pressing goes into the page. */
  hover: lift(-1),
  /** Document background — the glacier page. */
  page: palette.page,
  /** Cards, chart wells, and the ground the generated field is drawn on. */
  paper: lift(1),
  /** The brightest surface: popovers, menus, and inputs — what you write on. */
  raised: lift(2),
} as const;

export const lines = {
  /** Hairline borders. Ink at low alpha rather than a grey, so borders stay in
   *  the same hue family as the text they enclose. */
  hairline: mixOver(palette.ink, palette.page, 0.12),
  /** Card, hero and active borders. */
  hairlineStrong: mixOver(palette.ink, palette.page, 0.22),
} as const;

/**
 * Three text tiers, spaced DOWNWARD from the page rather than upward from the
 * AA floor.
 *
 * On a light ground the usable contrast range is lopsided: everything must clear
 * 4.5:1, so there is almost no room below `muted` (5.1:1) but a great deal of
 * room above it, all the way to 13.7:1. A tier added by lightening `muted` — the
 * obvious move, and the first thing written here — landed at 3.11:1, failing AA
 * outright; pulled up to clear it, it sat at 4.61:1, indistinguishable from the
 * 5.13:1 tier above. Two tiers a tenth of a ratio apart are one tier.
 *
 * So the scale hangs off the dark end instead, and `muted` — the exact token
 * mobile uses for its legal line — becomes the faintest tier rather than the
 * middle one.
 */
export const text = {
  /** Headlines and primary copy. ~13.7:1 on the page. */
  ink: palette.ink,
  /** Body copy. Sits between ink and muted so the three tiers are actually
   *  distinguishable; ~7.9:1. */
  inkDim: mixOver(palette.ink, palette.muted, 0.5),
  /** Field labels, captions, legal. Mobile's `muted` unchanged, ~5.1:1. */
  inkFaint: palette.muted,
} as const;

export const accent = {
  /** Graphics only — the active dot, the current step, a selected edge. */
  accent: palette.accent,
  /** The accent as TEXT. `accent` is only 4.6:1 on the page and fails AA for
   *  body sizes; this is the same hue darkened until it clears. Do not collapse
   *  these two into one token — that regression is invisible until measured. */
  accentInk: palette.accentInk,
  /** Hover for accent-filled graphics. */
  accentBright: adjustLightness(palette.accent, 0.06),
  /** Tinted fills: selected rows, soft badges. */
  accentSoft: mixOver(palette.accent, palette.page, 0.12),
  /** Accent-tinted borders. */
  accentLine: mixOver(palette.accent, palette.page, 0.32),
} as const;

export const button = {
  /** Primary CTA fill — near-black ink, NOT the accent. The accent marks
   *  position; the CTA is an action. Keeping them different is what stops the
   *  accent from meaning two things (design-language.md §1). */
  button: palette.button,
  buttonLabel: palette.buttonLabel,
  /** Hover. A near-black fill cannot brighten toward the page without going
   *  grey, so it lifts by a small step of its own lightness instead. */
  buttonHover: adjustLightness(palette.button, 0.06),
} as const;

/**
 * Status. The only hues in the system that are neither ink nor the acoustic
 * blue, permitted because they report an outcome rather than describe data —
 * a failed request, a satisfied constraint. They never encode a measurement.
 *
 * All three are pulled to a lightness that clears 4.5:1 as text on `page`; the
 * defaults a designer reaches for (a bright red, a spring green, a highlighter
 * yellow) do not. They also sit within 0.04 OKLCH lightness of each other, so a
 * readout that steps ok -> warn -> danger changes hue without appearing to
 * change weight — the step has to read as escalation, not as bolding.
 *
 * `warn` is the newest and exists for exactly one shape: a bounded allowance
 * running down (the compute-credit chip). An outcome that is not yet a failure
 * had no colour before it, and the two candidates on hand were both wrong —
 * `danger` cries wolf at 2-of-5 left, and the accent is spoken for (it marks
 * position in the flow, design-language.md §1).
 */
export const status = {
  danger: '#9E2B33',
  warn: '#7F5410',
  ok: '#2F6B45',
} as const;

/**
 * The listener — the human in the room — and the only sanctioned hue in the
 * system that is neither ink, the acoustic blue, nor a status outcome.
 *
 * The plan's default was to encode the seat by FORM (silhouette plus a floor
 * ring) rather than hue, on the grounds that a second accent dilutes the first.
 * That was overridden deliberately: in a dense 5.1.4 scene the seat is the one
 * object a user looks for directly, and silhouette alone does not survive a
 * cluttered plan view. Recorded here rather than left implicit so removing it
 * has to be a decision.
 *
 * Two constraints pin the value, and both are asserted in designTokens.test.ts:
 *
 *   - It must not be mistakable for `status.danger`. A red at the lightness
 *     that reads best on paper IS the danger red (both land near L 0.47), so
 *     the seat sits three ladder steps darker — a deep wine, not an alarm.
 *   - It must clear AA on every surface, because the legend labels it.
 *
 * On the old dark stage this was `--ed-accent2`, oklch(0.58 0.130 28). Same
 * hue family, reflected to the light ground.
 */
export const seat = '#7C2623';

/**
 * What shadows and scrims are made of.
 *
 * On paper this is ink, and the value is exactly what every shadow already
 * used. It is its own token because the two part ways in the dark theme: ink
 * turns light there, and a shadow mixed from light ink is a glow — a card
 * would appear to float on a halo. A shadow stays darker than the ground in
 * every theme; ink does not.
 */
export const depth = {
  shadow: palette.ink,
} as const;

export const designTokens = {
  ...surfaces,
  ...lines,
  ...text,
  ...accent,
  ...button,
  ...status,
  seat,
  ...depth,
} as const;

export type DesignToken = keyof typeof designTokens;
